/**
 * SidePanel — 오른쪽 상세 패널 (DESIGN-AI §16, 기준: Opp 상세 활동 패널)
 * 560px · 흰 면 · 왼쪽 1px 선 · 머리 14px/700 + 부제 12px · 30px 테두리 X · 본문 16px 패딩.
 * 바깥 클릭·ESC 로 닫는다. ds-scope 는 붙이지 않는다(본문의 기본 테두리를 지우지 않게) — 선은 border-solid 를 직접 적는다.
 *
 * ★ 모션: 열 때 오른쪽에서 밀려 들어온다(translateX 100%→0 · 280ms ease-out) + 백드롭 페이드.
 *   닫을 때는 open=false 직후 200ms 동안만 마지막 내용을 붙들고 밀려 나간다(그동안 클릭은 막는다).
 *   open 의미는 그대로 — 닫힘 처리(스크롤 락·ESC·포커스 트랩)는 open=false 순간 바로 풀린다.
 *   prefers-reduced-motion 이면 바로 열리고 바로 닫힌다(src/lib/motion.ts 의 t()).
 */
import React from "react";
import { X } from "lucide-react";
import { useModalDismiss } from "./useModalDismiss";
import { useLanguage } from "./LanguageProvider";
import { EASE } from "../../lib/motion";

/** 모션 시간 — 모션 줄이기면 0. matchMedia·animate 가 없는 환경(jsdom 테스트)에서도 터지지 않게 직접 감싼다. */
const motionMs = (ms: number) => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return 0;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : ms;
};

type Props = {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** 머리 오른쪽, X 앞 */
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  /** 바깥 클릭으로 닫기(기본 true). 입력 폼 패널은 false — 타이핑한 내용을 잃지 않게(§11 과 같은 이유) */
  dismissOnBackdrop?: boolean;
  children: React.ReactNode;
};

const ENTER_MS = 280;
const EXIT_MS = 200;
const BACKDROP = "rgba(8,15,28,0.45)";

type Snapshot = Pick<Props, "title" | "subtitle" | "headerActions" | "footer" | "children">;

export default function SidePanel({ open, onClose, title, subtitle, headerActions, footer, width = 560, dismissOnBackdrop = true, children }: Props) {
  const { t } = useLanguage();
  const { backdropProps, panelRef, zIndex } = useModalDismiss({ open, onClose, dismissOnEsc: true, dismissOnBackdrop });
  const backdropRef = React.useRef<HTMLDivElement>(null);
  // 그리고 있는지 — open 이 false 가 돼도 나가는 모션이 끝날 때까지 붙든다(DOM 을 갈아끼우지 않게 렌더 중에 맞춘다).
  const [shown, setShown] = React.useState(open);
  if (open && !shown) setShown(true);
  const leaving = !open && shown;
  // 나가는 동안은 마지막으로 열려 있던 때의 내용을 그린다(부모가 선택 상태를 먼저 비워도 빈 패널이 미끄러지지 않게).
  const snapshot = React.useRef<Snapshot>({ title, subtitle, headerActions, footer, children });
  if (open) snapshot.current = { title, subtitle, headerActions, footer, children };

  // 들어오기 — 열린 첫 프레임 전에 시작(깜빡임 없이)
  React.useLayoutEffect(() => {
    if (!open) return;
    const dur = motionMs(ENTER_MS);
    if (!dur) return;
    panelRef.current?.animate?.([{ transform: "translateX(100%)" }, { transform: "translateX(0)" }], { duration: dur, easing: EASE.out });
    backdropRef.current?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: dur, easing: EASE.out });
  }, [open, panelRef]);

  // 나가기 — open true→false 전환에서만
  React.useLayoutEffect(() => {
    if (!leaving) return undefined;
    const dur = motionMs(EXIT_MS);
    if (!dur) { setShown(false); return undefined; }
    const opts: KeyframeAnimationOptions = { duration: dur, easing: EASE.in, fill: "forwards" };
    const panelAnim = panelRef.current?.animate?.([{ transform: "translateX(0)" }, { transform: "translateX(100%)" }], opts);
    const backAnim = backdropRef.current?.animate?.([{ opacity: 1 }, { opacity: 0 }], opts);
    let alive = true;
    const finish = () => { if (alive) setShown(false); };
    if (panelAnim) panelAnim.finished.then(finish, finish); else finish();
    return () => {
      alive = false;
      // 나가는 도중 다시 열리면 붙어 있던 fill 을 걷어 낸다
      panelAnim?.cancel();
      backAnim?.cancel();
    };
  }, [leaving, panelRef]);

  if (!open && !shown) return null;
  const view = open ? { title, subtitle, headerActions, footer, children } : snapshot.current;
  return (
    <div {...(open ? backdropProps : {})} className="fixed inset-0 flex justify-end" style={{ zIndex, pointerEvents: open ? undefined : "none" }}>
      {/* 백드롭은 따로 깐다 — 패널과 별개로 페이드하려고(패널은 그 위에서 미끄러진다) */}
      <div ref={backdropRef} aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: BACKDROP }} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-hidden={open ? undefined : true}
        data-side-panel=""
        className="relative flex h-full flex-col border-0 border-l border-solid border-gray-200 bg-white"
        style={{ width: `min(${width}px, 100vw)`, boxShadow: "-12px 0 32px rgba(15,23,42,0.18)", willChange: "transform" }}
      >
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-0 border-b border-solid border-gray-200 px-4 py-3.5">
          <div className="min-w-0">
            <div className="text-[14px] font-bold text-gray-800">{view.title}</div>
            {view.subtitle != null ? <div className="truncate text-[12px] text-gray-500">{view.subtitle}</div> : null}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {view.headerActions}
            <button type="button" onClick={onClose} aria-label={t("닫기", "Close")}
              className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-md border border-solid border-gray-200 bg-white text-gray-500 outline-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-600/30">
              <X size={14} />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{view.children}</div>
        {view.footer != null ? <div className="flex flex-shrink-0 items-center justify-end gap-2 border-0 border-t border-solid border-gray-200 px-4 py-3">{view.footer}</div> : null}
      </div>
    </div>
  );
}
